// The admin page's Backups panel (backup.js on the server): when the database was last
// copied, a "Back up now" button, and a download link for each of the last 30 days.
import { useCallback, useEffect, useState } from 'react';
import { api } from './api';

const size = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`);
const when = (d) => new Date(d).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// Folded by default (remembered); the header still shows when the last backup ran.
const OPEN_KEY = 'mm-admin-backups-open';

export default function AdminBackups() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const load = useCallback(() => api('/admin/backups').then(setData).catch((err) => setError(err.message)), []);
  useEffect(() => { load(); }, [load]);

  async function backUpNow() {
    setBusy(true);
    setError('');
    setNote('');
    try {
      const b = await api('/admin/backups', { method: 'POST' });
      setNote(`Backed up ${b.docs.toLocaleString()} records (${size(b.bytes)}) in ${b.seconds.toFixed(1)}s.`);
      await load();
    } catch (err) {
      setError(err.message);
      load();
    } finally {
      setBusy(false);
    }
  }

  async function download(b) {
    setError('');
    try {
      const { url } = await api(`/admin/backups/${b.id}/download`);
      window.location.assign(url);
    } catch (err) {
      setError(err.message);
    }
  }

  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) === 'open';
    } catch {
      return false;
    }
  });
  function toggle() {
    setOpen((o) => {
      try {
        localStorage.setItem(OPEN_KEY, o ? 'closed' : 'open');
      } catch {
        // Not remembered in private mode.
      }
      return !o;
    });
  }
  // The briefing's "Open Backups" unfolds the panel.
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener('mm-open-backups', show);
    return () => window.removeEventListener('mm-open-backups', show);
  }, []);
  const list = data ? data.list : [];
  const last = list.find((b) => b.ok);
  const stale = !last || Date.now() - new Date(last.at).getTime() > 26 * 3600000;
  return (
    <section className="admin-panel" id="admin-backups" aria-labelledby="backups-title">
      <div className="admin-panel-header">
        <div>
          <h2 id="backups-title">
            <button type="button" className="panel-toggle" aria-expanded={open} aria-controls="backups-body" onClick={toggle}>
              <span className={`panel-toggle-chevron${open ? ' open' : ''}`} aria-hidden="true">&#9656;</span>
              Backups
              {data && <span className={`panel-toggle-count ${stale ? 'loss' : ''}`}>{last ? `${stale ? '⚠️' : '✓'} last ${when(last.at)}` : '⚠️ none yet'}</span>}
            </button>
          </h2>
          {open && <p className="admin-panel-sub">A full copy of the database every night at 3:00 AM Mérida time, kept {data ? data.keepDays : 30} days in your private Cloudflare storage</p>}
        </div>
        {open && <button type="button" className="small" onClick={backUpNow} disabled={busy || (data && !data.configured)}>{busy ? 'Backing up…' : 'Back up now'}</button>}
      </div>
      {open && (
      <div id="backups-body">
      {data && !data.configured && <p className="error">Cloudflare R2 storage isn't set up on the server, so backups can't run.</p>}
      {error && <p className="error">{error}</p>}
      {note && <p className="save-status success">{note}</p>}
      {data && (
        <p className={`backup-last ${stale ? 'stale' : 'fresh'}`}>
          {last ? <>{stale ? '⚠️' : '✓'} Last backup: <strong>{when(last.at)}</strong> · {size(last.bytes)} · {last.docs.toLocaleString()} records{stale ? ' · more than a day ago' : ''}</>
            : '⚠️ No backup yet. Click Back up now to make the first one.'}
        </p>
      )}
      {list.length > 0 && (
        <div className="table-wrap">
          <table className="backup-table">
            <thead><tr><th>When</th><th>Type</th><th>Size</th><th>Records</th><th aria-label="Download" /></tr></thead>
            <tbody>
              {list.map((b) => (
                <tr key={b.id} className={b.ok ? '' : 'backup-failed'}>
                  <td>{when(b.at)}</td>
                  <td>{b.reason === 'nightly' ? 'Nightly' : 'Manual'}</td>
                  <td>{b.ok ? size(b.bytes) : '—'}</td>
                  <td>{b.ok ? b.docs.toLocaleString() : <span className="loss">Failed: {b.error}</span>}</td>
                  <td>{b.ok && <button type="button" className="link-button" onClick={() => download(b)}>Download</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </div>
      )}
    </section>
  );
}
